import { lazy } from "react";

/** Count of full-page cache-bust navigations after chunk load failure (0…max). */
const STORAGE_KEY = "fractional_ui_chunk_rc";
const LEGACY_FLAG_KEY = "fractional_ui_chunk_recovery_reload";
const MAX_RECOVERY_NAVIGATIONS = 2;
const RETRY_DELAY_MS = 400;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getRecoveryCount() {
  try {
    sessionStorage.removeItem(LEGACY_FLAG_KEY);
    return Number.parseInt(sessionStorage.getItem(STORAGE_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function setRecoveryCount(n) {
  try {
    if (n <= 0) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, String(n));
    }
  } catch {
    /* private mode / unavailable */
  }
}

/**
 * Stale `index.html` (browser/CDN cache) often survives `reload()`; the document
 * still points at old hashed chunks → 404. A new URL forces a fresh fetch of
 * the HTML and correct chunk map.
 */
function navigateWithCacheBust() {
  const u = new URL(window.location.href);
  u.searchParams.set("fr", String(Date.now()));
  window.location.replace(u.toString());
}

async function loadWithRecovery(importFn) {
  try {
    const mod = await importFn();
    setRecoveryCount(0);
    return mod;
  } catch {
    try {
      await delay(RETRY_DELAY_MS);
      const mod = await importFn();
      setRecoveryCount(0);
      return mod;
    } catch (secondError) {
      try {
        const n = getRecoveryCount();
        if (n < MAX_RECOVERY_NAVIGATIONS) {
          setRecoveryCount(n + 1);
          navigateWithCacheBust();
          return new Promise(() => {});
        }
        setRecoveryCount(0);
      } catch {
        /* storage unavailable — fall through */
      }
      throw secondError;
    }
  }
}

/**
 * Like React.lazy, but retries the dynamic import once, then up to two full
 * navigations with cache-busting query params if chunks 404 (e.g. after deploy).
 */
export function lazyWithRecovery(importFn) {
  return lazy(() => loadWithRecovery(importFn));
}
